import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  Switch,
  HStack,
  Badge,
  useColorMode,
  useColorModeValue,
  Slider,
  SliderThumb,
  SliderTrack,
  SliderFilledTrack,
} from "@chakra-ui/react";
import { FiCheck } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PublicNavbar } from "./layout/PublicNavbar";
import { Footer } from "./layout/Footer";
import { userAPI } from "../services";
import { TypeAnimation } from "react-type-animation";

interface PricingCardProps {
  name: string;
  description: string;
  price?: string;
  yearlyPrice?: string;
  period?: string;
  yearlyPeriod?: string;
  features: string[];
  isPopular?: boolean;
  isYearly: boolean;
  onGetStarted: () => void;
  isEnterprise?: boolean;
  isActive?: boolean;
}

const PricingCard: React.FC<PricingCardProps> = ({
  name,
  description,
  price,
  yearlyPrice,
  period,
  yearlyPeriod,
  features,
  isPopular,
  isYearly,
  onGetStarted,
  isEnterprise,
  isActive,
}) => {
  const { t } = useTranslation();
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const popularBg = useColorModeValue("brand.primary", "brand.secondary");
  const textColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Box
      position="relative"
      bg={cardBg}
      border="1px"
      borderColor={isPopular ? popularBg : borderColor}
      borderRadius="xl"
      p={8}
      shadow={isPopular ? "xl" : "md"}
      transform={isPopular ? "scale(1.05)" : undefined}
      zIndex={isPopular ? 1 : 0}
      transition="all 0.3s"
      _hover={{
        transform: isPopular ? "scale(1.08)" : "scale(1.03)",
        shadow: "xl",
      }}
    >
      {isPopular && (
        <Badge
          position="absolute"
          top="-3"
          right="6"
          px={3}
          py={1}
          bg={popularBg}
          color={textColor}
          borderRadius="full"
          fontWeight="bold"
        >
          {t("pricing.mostPopular")}
        </Badge>
      )}
      {isActive && (
        <Badge
          position="absolute"
          top="-3"
          left="6"
          px={3}
          py={1}
          colorScheme="green"
          borderRadius="full"
          fontWeight="bold"
        >
          {t("pricing.currentPlan")}
        </Badge>
      )}

      <VStack spacing={5} align="stretch">
        <VStack spacing={2} align="stretch">
          <Heading size="lg" fontFamily="Nasalization">
            {name}
          </Heading>
          <Text color={textColor}>{description}</Text>
        </VStack>

        <VStack spacing={1} align="stretch">
          <Heading fontSize="33px" fontFamily="Nasalization">
            {isYearly && yearlyPrice ? yearlyPrice : price}
          </Heading>
          {(period || yearlyPeriod) && (
            <Text color={textColor}>{isYearly ? yearlyPeriod : period}</Text>
          )}
        </VStack>

        <Button
          size="lg"
          colorScheme={isPopular ? "brand" : "gray"}
          onClick={onGetStarted}
          fontFamily="Nasalization"
          isDisabled={isActive}
        >
          {isActive
            ? t("pricing.currentPlan")
            : isEnterprise
            ? t("pricing.contactUs")
            : t("pricing.getStarted")}
        </Button>

        <List spacing={3} mt={6}>
          {Array.isArray(features) &&
            features.map((feature, index) => (
              <ListItem key={index} display="flex" alignItems="center">
                <ListIcon as={FiCheck} color="green.500" boxSize={5} />
                <Text>{feature}</Text>
              </ListItem>
            ))}
        </List>
      </VStack>
    </Box>
  );
};

export const Pricing: React.FC = () => {
  const [isYearly, setIsYearly] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const bgColor = useColorModeValue("gray.50", "gray.900");

  useEffect(() => {
    const loadUserPlan = async () => {
      if (localStorage.getItem("token")) {
        try {
          const user = await userAPI.getCurrentUser();
          setCurrentPlan(user.plan);
        } catch (error) {
          console.error("Error loading user plan:", error);
          setCurrentPlan(null);
        }
      }
    };
    loadUserPlan();
  }, []);

  const handleGetStarted = (tier: string) => {
    if (tier === "enterprise") {
      navigate("/soon");
    } else if (tier === "prepaid") {
      navigate("/payments/add-funds");
    } else if (tier === "subscription") {
      navigate("/payments/switch-plan");
    } else {
      navigate("/register");
    }
  };

  const getFeatures = (key: string): string[] => {
    const features = t(`pricing.tiers.${key}.features`, {
      returnObjects: true,
    });
    return Array.isArray(features) ? features : [];
  };

  const AutoScrollSlider = () => {
    const textColor = useColorModeValue("gray.600", "gray.400");
    const items = t("pricing.canBeDoneWith36Credits", {
      returnObjects: true,
    }) as { title: string }[];

    // Create sequence array for TypeAnimation
    const sequence = items.flatMap((item): (string | number)[] => [
      item.title,
      3000, // pause for 3 seconds
    ]);

    return (
      <Box h="60px" maxW="2xl" mx="auto" textAlign="center">
        <TypeAnimation
          sequence={sequence}
          wrapper="span"
          speed={89}
          style={{
            fontSize: "40px",
            display: "inline-block",
            fontFamily: "Nasalization",
            color: useColorModeValue("#00392b", "#e1fa00"),
          }}
          repeat={Infinity}
        />
      </Box>
    );
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PublicNavbar />

      <Box flex="1" bg={bgColor} py={40}>
        <Container maxW="container.xl">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("pricing.title")}
              </Heading>
              <Text
                fontSize="xl"
                color={useColorModeValue("gray.600", "gray.400")}
                maxW="2xl"
              >
                {t("pricing.subtitle")}
              </Text>

              {/* <HStack spacing={4} pt={8}>
                <Text>{t("pricing.monthly")}</Text>
                <Switch
                  size="lg"
                  isChecked={isYearly}
                  onChange={() => setIsYearly(!isYearly)}
                  colorScheme="brand"
                />
                <Text>{t("pricing.yearly")}</Text>
                <Badge colorScheme="green" variant="solid" ml={2}>
                  {t("pricing.yearlyDiscount")}
                </Badge>
              </HStack> */}
            </VStack>

            <SimpleGrid
              columns={{ base: 1, lg: 3 }}
              spacing={{ base: 8, lg: 4 }}
              maxW="7xl"
              mx="auto"
              w="full"
            >
              <PricingCard
                name={t("pricing.tiers.free.name")}
                description={t("pricing.tiers.free.description")}
                price={t("pricing.tiers.free.price")}
                features={getFeatures("free")}
                isYearly={isYearly}
                onGetStarted={() => handleGetStarted("free")}
                isActive={currentPlan === "FREE"}
              />
              <PricingCard
                name={t("pricing.tiers.prepaid.name")}
                description={t("pricing.tiers.prepaid.description")}
                price={t("pricing.tiers.prepaid.price")}
                features={getFeatures("prepaid")}
                isYearly={isYearly}
                isPopular={true}
                onGetStarted={() => handleGetStarted("prepaid")}
                isActive={currentPlan === "PREPAID"}
              />
              {/* <PricingCard
                name={t("pricing.tiers.subscription.name")}
                description={t("pricing.tiers.subscription.description")}
                price={t("pricing.tiers.subscription.price")}
                yearlyPrice={t("pricing.tiers.subscription.yearlyPrice")}
                period={t("pricing.tiers.subscription.period")}
                yearlyPeriod={t("pricing.tiers.subscription.yearlyPeriod")}
                features={getFeatures("subscription")}
                isPopular={false}
                isYearly={isYearly}
                onGetStarted={() => void 0}
                // onGetStarted={() => handleGetStarted("subscription")}
                isActive={currentPlan === "PRO"}
              /> */}
              <PricingCard
                name={t("pricing.tiers.enterprise.name")}
                description={t("pricing.tiers.enterprise.description")}
                // price={t('pricing.tiers.enterprise.price')}
                features={getFeatures("enterprise")}
                isYearly={isYearly}
                onGetStarted={() => handleGetStarted("enterprise")}
                isEnterprise={true}
                isActive={currentPlan === "ENTERPRISE"}
              />
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>
      <Box
        flex="1"
        bg={colorMode === "dark" ? "gray.800" : "gray.100"}
        py={20}
        mb={20}
      >
        <Container maxW="container.xl">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center">
              {/* <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("pricing.secondaryTitle")}
              </Heading> */}
              <Text
                fontSize="xl"
                color={useColorModeValue("gray.600", "gray.400")}
                maxW="2xl"
              >
                {t("pricing.secondaryTitle")}
              </Text>
              <AutoScrollSlider />
            </VStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};
